import React, { type ReactNode } from "react";
import { type LinkProps, Flex, Text } from "@chakra-ui/react";
import { formatDate } from "utils";
import Balancer from "react-wrap-balancer";
import { remCalc } from "theme/utils";
import { Link } from "ui/link";

type FeaturedResourceCardProps = {
  href: LinkProps["href"];
  publishedAt: string;
  title: string;
  excerpt: string;
  image: ReactNode;
};

export function FeaturedResourceCard({ image, href, title, publishedAt, excerpt }: FeaturedResourceCardProps) {
  return (
    <Link
      variant="unstyled"
      href={href}
      aria-label={`Read ${title}.`}
      display="flex"
      boxSize="100%"
      maxW={{ base: "cardMaxW", md: "100%" }}
      mx="auto"
      borderRadius="big"
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        alignItems="stretch"
        boxSize="100%"
        boxShadow="card"
        border="1px"
        borderColor="new.gray.150"
        borderRadius="big"
        overflow="hidden" // Hide image border overflow
      >
        <Flex flex={1}>{image}</Flex>

        <Flex
          flex={{ base: 1, md: 0.8 }}
          direction="column"
          gap="smallest"
          justify="center"
          p="small"
          borderTop={{ base: "1px solid", md: "none" }}
          borderTopColor={{ base: "new.gray.150", md: "transparent" }}
          borderLeft={{ base: "none", md: "1px solid" }}
          borderLeftColor={{ base: "transparent", md: "new.gray.150" }}
          minH={{ base: "auto", md: remCalc(270) }} // Min height avoids layout shift when filtering posts
        >
          <Text textStyle="overline1" fontWeight="bold" color="brand.textSecondary.light">
            {formatDate(publishedAt)}
          </Text>

          <Text as="h2" textStyle="headline2">
            <Balancer>{title}</Balancer>
          </Text>

          <Text color="brand.textSecondary.light" textStyle="caption1" mt="tiny">
            {excerpt}
          </Text>
        </Flex>
      </Flex>
    </Link>
  );
}
